/* eslint-disable max-len */
/* stylelint-disable max-line-length */
import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {flipOnLTR, media, rotateOnLTR} from '~/shared/theme/utils';
import scooberTag from '~/assets/images/icons/scoober_tag.svg';
import restaurantTag from '~/assets/images/icons/restaurant_tag.svg';
import {body14Bold} from '~/shared/theme/typography';
import {flexCenter} from '~/shared/theme/FlexLayout';
import {useIsMinLargeMobile, useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';

type RestaurantFeaturesProps = {isDeliveryTracker?: boolean; isStore?: boolean};

const DeliveryTrackerLogoIcon = styled(ImageWithAlt).attrs({noAlt: true})``;

const ScooberWrapper = styled.div.attrs({className: 'transparent-bg-on-contrast'})`
  position: absolute;
  display: flex;
  border-radius: 100px;
  align-items: center;
  gap: 4px;
  padding: 2px;
  background: ${({theme}) => theme.colors.primaryText};
  box-shadow: ${({theme}) => theme.shadows.shadow9};
  z-index: 80;
  ${flipOnLTR`
    padding-left: 6px;
  `}
`;

const ScooberMenuWrapper = styled(ScooberWrapper)`
  bottom: 16px;
  ${flipOnLTR`
    left: 16px;
    ${media.minLargeMobile`
      bottom: 0;
      left: 0;
    `}
  `}
`;

const CenteredWrapper = styled.div`
  width: 570px;
  margin: auto auto 16px;
`;

const ScooberRestaurantCardWrapper = styled(ScooberWrapper)`
  bottom: 12px;
  ${flipOnLTR`
    left: 12px;
  `}
`;

const DeliveryTrackerText = styled.div`
  ${body14Bold}
  color: ${({theme}) => theme.colors.charcoal80};
`;

const ScooberTextRestaurant = styled(DeliveryTrackerText)`
  line-height: 1;
`;

const IconWrapper = styled.div<{darkBg?: boolean}>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({theme, darkBg}) => (darkBg ? theme.colors.backgroundInteractive : theme.colors.contentBrand)};
  ${flexCenter}
  ${rotateOnLTR}
`;

const DiagonalBadge = memo(({isDeliveryTracker, isStore}: RestaurantFeaturesProps) => {
  const {t} = getLocalizationService();
  return (
    <ScooberMenuWrapper>
      <IconWrapper darkBg={!isDeliveryTracker}>
        <DeliveryTrackerLogoIcon src={isDeliveryTracker ? scooberTag : restaurantTag} />
      </IconWrapper>
      <DeliveryTrackerText>{t(isDeliveryTracker ? 'brandName_delivery' : isStore ? 'own_delivery_store' : 'own_delivery')}</DeliveryTrackerText>
    </ScooberMenuWrapper>
  );
});

const RestaurantCardBadge = memo(({isDeliveryTracker, isStore}: RestaurantFeaturesProps) => {
  const {t} = getLocalizationService();
  return (
    <ScooberRestaurantCardWrapper>
      <IconWrapper darkBg={!isDeliveryTracker}>
        <DeliveryTrackerLogoIcon src={isDeliveryTracker ? scooberTag : restaurantTag} />
      </IconWrapper>
      <ScooberTextRestaurant>{t(isDeliveryTracker ? 'brandName_delivery' : isStore ? 'own_delivery_store' : 'own_delivery')}</ScooberTextRestaurant>
    </ScooberRestaurantCardWrapper>
  );
});

const DeliveryBadge = ({isDeliveryTracker, top, isStore}: {isDeliveryTracker?: boolean; top?: boolean; isStore?: boolean}) => {
  const isMinLargeMobile = useIsMinLargeMobile();
  const isMinLargeTablet = useIsMinLargeTablet();

  const centerLogo = isMinLargeMobile && !isMinLargeTablet;
  if (!top) {
    return <RestaurantCardBadge isStore={isStore} isDeliveryTracker={isDeliveryTracker} />;
  }
  if (centerLogo) {
    return (
      <CenteredWrapper>
        <DiagonalBadge isStore={isStore} isDeliveryTracker={isDeliveryTracker} />
      </CenteredWrapper>
    );
  }
  return <DiagonalBadge isStore={isStore} isDeliveryTracker={isDeliveryTracker} />;
};

export default memo(DeliveryBadge);
